import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { GoX } from 'react-icons/go';
import { LuBot } from 'react-icons/lu';

import {
  AGENT_PERMISSIONS, agentDraft, centredScrollLeft, customAgentBlock, effectiveAgent, resolvableHarnesses,
  tickLabels,
} from '../domain/cellWorkflow';

/**
 * Resolves the DOM rect CellWorkflowSection / MissionPanel anchor the popover to, given the graph
 * preview's container and the tapped step's id.
 *
 * The tapped element is the `.mc-workflow-agent` BUTTON, but cells.css gives it `display: contents`
 * (see its comment there) so the button itself generates no box — `getBoundingClientRect()` on it
 * returns an all-zero rect, which reads to AgentPopover as "anchor at the viewport's top-left
 * corner" rather than "anchor on the chip". The flow-kit tag rendered INSIDE the button (BhFlowTag's
 * `.bh-flow-node-tag` span) is what actually has geometry, so that is what gets measured.
 *
 * Guards against a zero-area rect regardless of source — display:contents recurring, a detached
 * node, anything — so a future break here fails safe (AgentPopover's own documented fallback) rather
 * than silently reopening the popover in a screen corner.
 */
export function chipAnchorRect(container, stepId) {
  const chip = container && container.querySelector(`.mc-workflow-agent[data-step-id="${stepId}"]`);
  const tag = chip && chip.querySelector('.bh-flow-node-tag');
  const rect = tag && tag.getBoundingClientRect();
  return (rect && rect.width > 0 && rect.height > 0) ? rect : null;
}

// Kept clear of every viewport edge the popover is pushed off of, so it never sits flush against one.
const VIEWPORT_MARGIN = 8;
// Gap between the chip and the popover it opens.
const ANCHOR_GAP = 4;

/**
 * AgentPopover — the agent picker, anchored to the chip that opened it.
 *
 * Portalled to `document.body` — the move that lets this be a popover at all. Anchoring it inside
 * the ReactFlow canvas (the original objection to a popover) has it clipped by the canvas's own
 * overflow/stacking context; portalling escapes that entirely, and the popover is positioned purely
 * from `anchorRect` (the tapped chip's own `getBoundingClientRect()`), which travels with it across
 * the portal boundary since CSS layout can no longer relate the two.
 *
 * One component, used by both CellWorkflowSection and MissionPanel — the two panels do not share
 * controls anywhere else, but duplicating this one is exactly how they would drift apart.
 *
 * Closes on picking a row, the ×, a click outside, or Escape — all funneled through `onClose`, which
 * the caller uses to clear whatever state is tracking "which step is picked".
 *
 * Props:
 *   anchorRect    — DOMRect | null — the chip's own rect; falls back to the viewport's top-left
 *                   corner when absent, rather than not rendering, since the popover's OPEN/CLOSED
 *                   state is the caller's (whether it mounts this at all). The popover is then
 *                   CLAMPED into the viewport — see VIEWPORT_MARGIN below.
 *   stepId        — the step this chooses an agent for, named in the header
 *   agents        — resolvableAgents(...) — only entries agentSelection can actually resolve
 *   selectedAlias — '' for no agent, 'custom' for an alias-less block, or a real alias — decides
 *                   which row (if any) carries the check mark
 *   onPick(alias) — a real agent row was chosen
 *   onClose()     — × / Escape / outside click
 *   harnesses     — the RAW `harnesses` palette; the component resolves it itself, and an empty
 *                   vocabulary means the whole custom half does not exist — see below
 *   currentAgent  — the picked step's existing agent block, or null; where the sliders start
 *   onBuild(block)— a slider was RELEASED, producing an alias-less five-field block
 *
 * **The custom half is conditional on the vocabulary.** With no resolvable harness there is no
 * section, no separator, and no width change — the popover is exactly the preset list it was
 * before, which is what makes this inert on an instance whose palette predates the `harnesses` key.
 */

// Where the sliders sit when there is nothing to read them off — no agent on the step, or one the
// vocabulary cannot represent. Index 0 of every scale, and every scale is ordered least-to-most, so
// this is the most conservative agent the palette can describe rather than an arbitrary one.
const DEFAULT_DRAFT = { harness: 0, model: 0, effort: 0, permissions: 0 };

// An index into a list that is always in range. The draft carries indices while the lists under
// them can change (a harness swap re-populates two of the four), so every read goes through this
// rather than trusting the pair to stay in step.
function at(values, index) {
  return Math.min(Math.max(index, 0), values.length - 1);
}

// Whether `viewport` currently has more content past either edge. The 1px slack on the right absorbs
// sub-pixel rounding — without it a strip scrolled exactly to its end can measure a fraction of a
// pixel short and leave the fade stuck on.
function readScrollability(viewport) {
  return {
    left: viewport.scrollLeft > 0,
    right: viewport.scrollLeft < viewport.scrollWidth - viewport.clientWidth - 1,
  };
}

// The viewport's own class suffix for the edge fade — a scale with nothing tracked yet (not overflowing,
// or not measured) gets neither, which is the class list a fitting scale has always had.
function scrollabilityClass(edges) {
  if (!edges) return '';
  return `${edges.left ? ' is-scrollable-left' : ''}${edges.right ? ' is-scrollable-right' : ''}`;
}

export default function AgentPopover({
  anchorRect, stepId, agents, selectedAlias, onPick, onClose, harnesses, currentAgent, onBuild,
}) {
  const ref = useRef(null);
  const [placement, setPlacement] = useState(null);
  // One viewport + row DOM node per scale, keyed by scale.key. The four scale keys ('harness',
  // 'model', 'effort', 'permissions') are fixed for the lifetime of a mount, so a map survives a
  // harness swap without losing track of which node belongs to which scale — and it is a map rather
  // than four separate refs because the scales themselves come from an array.
  const viewportRefs = useRef({});
  const rowRefs = useRef({});
  // Whether a scale's row is wider than its viewport, per scale.key — a harness swap can flip one
  // scale into overflow while its siblings stay put. Drives the `.is-strip` class; never guessed
  // from the stop count, only from the measured boxes.
  const [overflowing, setOverflowing] = useState({});
  // Which scales have been positioned at least once, per scale.key — the popover's first paint
  // snaps the strip straight to the selection; only a later change (a drag, a resize) glides.
  const firstLayout = useRef({});
  // The values list `firstLayout` was last set against, per scale.key — a harness swap replaces
  // `model`/`effort` outright, and that new list deserves its own unanimated first layout rather
  // than gliding in from wherever the old list happened to leave the strip.
  const lastValues = useRef({});
  // Whether a scale's strip has more content past either edge, per scale.key — drives the edge fade
  // that says "this continues" rather than reading like the very clipping bug the strip exists to
  // fix. Recomputed after every centring AND on the viewport's own `scroll` event, since a user can
  // drag the strip directly without moving a slider at all.
  const [scrollability, setScrollability] = useState({});
  // Re-invoked by the ResizeObserver below, which is a plain DOM callback outside React's render —
  // it reads this ref rather than closing over a stale `measure` from whichever render set it up.
  const measureRef = useRef(() => {});

  const vocabulary = resolvableHarnesses(harnesses);
  const building = vocabulary.length > 0;
  // Null means the step's block cannot be REPRESENTED on these scales — an unknown harness, or a
  // model its own harness does not list. Not clamped to index 0: sliders resting on `haiku` while
  // the step runs `opus` would state something false about what it will do. Seeded at the defaults
  // and said out loud instead, just below the section header.
  // Resolved through the palette first: before the freeze a step carries `agent: {alias: claude}`,
  // not the five fields, and drafting straight off that reads a reference as an unrecognisable block —
  // which showed the "not in the vocabulary" line on a step plainly running a listed preset.
  const seed = agentDraft(effectiveAgent(currentAgent, agents), harnesses);
  const [draft, setDraft] = useState(() => seed || DEFAULT_DRAFT);
  const adrift = !!currentAgent && seed === null;

  useEffect(() => {
    const onMouseDown = (event) => {
      if (ref.current && !ref.current.contains(event.target)) onClose();
    };
    const onKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', onMouseDown, true);
    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('mousedown', onMouseDown, true);
      document.removeEventListener('keydown', onKeyDown, true);
    };
  }, [onClose]);

  const rect = anchorRect || { bottom: 0, left: 0, top: 0 };

  // The chip is anchored wherever the graph put it, which for a step on the right of a wide workflow
  // is close enough to the viewport edge that a 220px popover hangs off it — the model/effort column
  // is the first thing lost, and it is the column the choice is actually made on. Measured rather
  // than assumed from the CSS width, so a change to the popover's own styling cannot silently
  // reintroduce the overflow. useLayoutEffect runs before paint, so the clamp never flickers.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const { width, height } = el.getBoundingClientRect();
    const left = Math.max(
      VIEWPORT_MARGIN, Math.min(rect.left, window.innerWidth - width - VIEWPORT_MARGIN),
    );
    // Prefer below the chip; flip above only when below would run off and above actually fits, so a
    // popover taller than the space either way still opens downward rather than over its own chip.
    let top = rect.bottom + ANCHOR_GAP;
    if (top + height > window.innerHeight - VIEWPORT_MARGIN) {
      const above = rect.top - height - ANCHOR_GAP;
      if (above >= VIEWPORT_MARGIN) top = above;
    }
    setPlacement({ top, left });
    // `building` is in the deps because it changes the popover's WIDTH (220px to 280px, via the
    // is-building modifier), and the clamp above is computed from the measured box. A vocabulary
    // that arrives while the popover is already open would otherwise widen it past the right edge
    // with a placement measured against the narrow one.
  }, [rect.left, rect.top, rect.bottom, building]);

  const style = placement
    ? { position: 'fixed', top: placement.top, left: placement.left }
    : { position: 'fixed', top: rect.bottom + ANCHOR_GAP, left: rect.left };

  // The four scales, in the order they are read: which harness, then the two things it alone
  // defines, then the one ladder every harness shares. `models`/`efforts` come off the CURRENT
  // harness — that is the whole reason the harness scale is first.
  const harness = building ? vocabulary[at(vocabulary, draft.harness)] : null;
  const scales = harness ? [
    { key: 'harness', name: 'Harness', values: vocabulary.map(entry => entry.alias) },
    { key: 'model', name: 'Model', values: harness.models },
    { key: 'effort', name: 'Effort', values: harness.efforts },
    { key: 'permissions', name: 'Permissions', values: AGENT_PERMISSIONS },
  ] : [];
  // A VALUE-identity signature for the scales, since `scales` above is a fresh array of fresh
  // objects every render — depending on it directly would re-run the measuring effect below (and
  // therefore re-centre) on every unrelated re-render, fighting anyone hand-panning the strip. This
  // only changes when a harness swap actually replaces a value list.
  const scalesSignature = JSON.stringify(scales.map(scale => [scale.key, ...scale.values]));

  /**
   * Measures every scale's row against its viewport and, for one that overflows, centres the active
   * tick — the strip's whole job. Reads the DOM directly (this is the one place that may: the pure
   * function it hands off to, `centredScrollLeft`, is what stays testable) and writes `scrollLeft`
   * directly, never `scrollIntoView`, which would also scroll the page behind the popover.
   *
   * `animate` is false for a ResizeObserver callback (a resize is not a gesture worth a glide) and
   * true for a render-driven call, which itself glides only from the SECOND time a given scale is
   * positioned — the popover's first paint must not open mid-glide — and never when the viewer has
   * asked for reduced motion.
   */
  const measure = ({ animate }) => {
    const reducedMotion = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
    const next = {};
    const nextScrollability = {};
    scales.forEach(scale => {
      const viewport = viewportRefs.current[scale.key];
      const row = rowRefs.current[scale.key];
      if (!viewport || !row) return;
      // A harness swap can hand this scale.key an entirely new values list (model/effort) — that
      // list has never been laid out before, so it earns its own unanimated first layout rather
      // than gliding in from the old list's position.
      const valuesKey = JSON.stringify(scale.values);
      if (lastValues.current[scale.key] !== valuesKey) {
        firstLayout.current[scale.key] = false;
        lastValues.current[scale.key] = valuesKey;
      }
      const isOverflowing = row.scrollWidth > viewport.clientWidth;
      next[scale.key] = isOverflowing;
      if (isOverflowing) {
        const activeTick = row.children[at(scale.values, draft[scale.key])];
        if (activeTick) {
          const viewportRect = viewport.getBoundingClientRect();
          const tickRect = activeTick.getBoundingClientRect();
          const target = centredScrollLeft({
            scrollLeft: viewport.scrollLeft,
            viewportWidth: viewport.clientWidth,
            contentWidth: row.scrollWidth,
            labelLeft: tickRect.left - viewportRect.left,
            labelWidth: tickRect.width,
          });
          const glide = animate && firstLayout.current[scale.key] && !reducedMotion
            && typeof viewport.scrollTo === 'function';
          firstLayout.current[scale.key] = true;
          if (glide) {
            viewport.scrollTo({ left: target, behavior: 'smooth' });
          } else {
            viewport.scrollLeft = target;
          }
        }
      }
      nextScrollability[scale.key] = readScrollability(viewport);
    });
    setOverflowing(prev => {
      const keys = Object.keys(next);
      const same = keys.length === Object.keys(prev).length && keys.every(key => prev[key] === next[key]);
      return same ? prev : next;
    });
    setScrollability(prev => {
      const keys = Object.keys(nextScrollability);
      const same = keys.length === Object.keys(prev).length && keys.every(key => (
        prev[key] && prev[key].left === nextScrollability[key].left && prev[key].right === nextScrollability[key].right
      ));
      return same ? prev : nextScrollability;
    });
  };

  // `measure` is rebuilt every render, so keeping `measureRef` current is a render-phase
  // side effect and belongs in an effect, not inline in the render body. No deps: the
  // ResizeObserver and scroll-listener effects below (and the one right after this) call through
  // the ref precisely so they always reach whichever `measure` this render just built.
  useLayoutEffect(() => {
    measureRef.current = measure;
  });

  // Re-measures whenever a scale's values change (a harness swap replaces the model/effort lists
  // outright) or the draft moves (a drag, a key press). useLayoutEffect so the pan never flickers.
  //
  // Deps are `scalesSignature` (a VALUE-identity string), not `scales` itself: `scales` is a fresh
  // array of fresh objects every render, so depending on it re-runs this — and therefore re-centres
  // — on every unrelated re-render (a resize's own `setOverflowing`, the scroll listener's
  // `setScrollability`, a parent re-render). That fights anyone hand-panning the strip: a drag flips
  // a scrollability flag, which re-renders, which used to re-centre and pull the strip straight
  // back to the selection. `draft` needs no such treatment — it is `useState`, so its identity is
  // already stable across any render that did not call `setDraft`.
  useLayoutEffect(() => {
    measure({ animate: true });
    // `measure` closes over `scales`/`draft`, which the signature and `draft` deps already track;
    // listing the (recreated-every-render) function itself would only make this re-run for a reason
    // it already does.
  }, [scalesSignature, draft]); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-measures on resize. Kept to `building` rather than a full exhaustive-deps list — like the
  // placement effect above — so the observer is created once per mount of the custom section rather
  // than torn down and rebuilt on every draft change.
  useEffect(() => {
    if (typeof ResizeObserver === 'undefined' || !building) return undefined;
    const observer = new ResizeObserver(() => measureRef.current({ animate: false }));
    Object.values(viewportRefs.current).forEach(el => { if (el) observer.observe(el); });
    return () => observer.disconnect();
  }, [building]);

  // A user can drag the strip directly, which fires no React event at all — this is the only way the
  // edge fade learns about it. Reads `scrollLeft` off the viewport that fired, so it never depends on
  // `draft`, and is scoped to `building` for the same reason the ResizeObserver effect is.
  useEffect(() => {
    if (!building) return undefined;
    const bound = Object.entries(viewportRefs.current)
      .filter(([, viewport]) => !!viewport)
      .map(([key, viewport]) => {
        const onScroll = () => {
          const value = readScrollability(viewport);
          setScrollability(prev => {
            const current = prev[key];
            if (current && current.left === value.left && current.right === value.right) return prev;
            return { ...prev, [key]: value };
          });
        };
        viewport.addEventListener('scroll', onScroll);
        return { viewport, onScroll };
      });
    return () => bound.forEach(({ viewport, onScroll }) => viewport.removeEventListener('scroll', onScroll));
  }, [building]);

  // Moving a slider updates LOCAL state only. onChange fires per input event — continuously through
  // a drag — so a write here would post one save per pixel; the write goes on release, below.
  const move = (key, raw) => {
    const index = Number(raw);
    setDraft(prev => {
      if (key !== 'harness') return { ...prev, [key]: index };
      // A different harness brings its own two lists. CLAMPED into them rather than reset to 0, so
      // somebody on the deepest model of one harness lands on the deepest of the next instead of
      // being dropped to the shallowest every time they compare two harnesses.
      const next = vocabulary[at(vocabulary, index)];
      return {
        harness: index,
        model: Math.min(prev.model, next.models.length - 1),
        effort: Math.min(prev.effort, next.efforts.length - 1),
        permissions: prev.permissions,
      };
    });
  };

  // Release — pointer-up (drag and click-on-track alike) and key-up (the keyboard's equivalent) —
  // is the gesture that MEANS something, so it is the one that writes.
  const release = () => {
    if (!harness || !onBuild) return;
    const block = customAgentBlock(
      harness,
      harness.models[at(harness.models, draft.model)],
      harness.efforts[at(harness.efforts, draft.effort)],
      AGENT_PERMISSIONS[at(AGENT_PERMISSIONS, draft.permissions)],
    );
    if (block) onBuild(block);
  };

  // Which of the two halves the step's agent currently came from. A preset row is named; anything
  // else — a block built here, one built through the API, a step with no agent — has no name to
  // give, and saying so is more use than showing a blank.
  const fromPreset = (agents || []).some(agent => agent.alias === selectedAlias);

  return ReactDOM.createPortal(
    <div className={`mc-agent-popover${building ? ' is-building' : ''}`} style={style} ref={ref}>
      <div className="mc-agent-popover-header">
        <span className="mc-agent-popover-title">{`Agent for ${stepId}`}</span>
        <button type="button" className="mc-agent-popover-close" aria-label="Close" onClick={onClose}>
          <GoX />
        </button>
      </div>
      <div className="mc-agent-popover-list">
        {(agents || []).map(agent => (
          <button
            type="button"
            key={agent.alias}
            className={`mc-agent-popover-row${agent.alias === selectedAlias ? ' is-selected' : ''}`}
            onClick={() => onPick(agent.alias)}
          >
            <LuBot size={11} className="mc-agent-popover-icon" />
            <span className="mc-agent-popover-alias">{agent.alias}</span>
            <span className="mc-agent-popover-meta">{`${agent.model} · ${agent.effort}`}</span>
            {agent.alias === selectedAlias && <span className="mc-agent-popover-check">✓</span>}
          </button>
        ))}
      </div>
      {building && (
        <>
          <div className="mc-agent-popover-sep" />
          <div className="mc-agent-popover-build">
            <div className="mc-agent-popover-build-header">
              <span className="mc-agent-popover-build-title">Custom</span>
              <span className="mc-agent-popover-build-state">
                {fromPreset ? `showing ${selectedAlias}` : 'custom — no alias'}
              </span>
            </div>
            {adrift && (
              <div className="mc-agent-popover-build-note">
                This step&rsquo;s agent is not in the palette&rsquo;s vocabulary. Moving a slider replaces it.
              </div>
            )}
            {scales.map(scale => (
              <div className="mc-agent-popover-scale" key={scale.key}>
                <div className="mc-agent-popover-scale-name">
                  <span>{scale.name}</span>
                  {/* The value IN FULL, here, once — which is what lets every tick below stay short
                      enough to read whole rather than being clipped into ambiguity. */}
                  <span className="mc-agent-popover-scale-value">
                    {scale.values[at(scale.values, draft[scale.key])]}
                  </span>
                </div>
                <div
                  className={`mc-agent-popover-ticks${scrollabilityClass(scrollability[scale.key])}`}
                  ref={el => { viewportRefs.current[scale.key] = el; }}
                >
                  <div
                    className={`mc-agent-popover-ticks-row${overflowing[scale.key] ? ' is-strip' : ''}`}
                    ref={el => { rowRefs.current[scale.key] = el; }}
                  >
                    {tickLabels(scale.values).map((label, index) => (
                      <span
                        className={`mc-agent-popover-tick${
                          // Restricted to strip mode: on a fitting row the value is already stated
                          // in full on the name row above, so marking a tick here would only jitter
                          // the space-between spread on every slider move — and, worse, widen the
                          // row depending on which stop is bold, feeding the selection back into the
                          // very overflow measurement that decides whether the row IS a strip.
                          overflowing[scale.key] && index === at(scale.values, draft[scale.key]) ? ' is-on' : ''}`}
                        data-testid="agent-scale-tick"
                        key={scale.values[index]}
                        title={scale.values[index]}
                      >{label}</span>
                    ))}
                  </div>
                </div>
                <input
                  type="range"
                  className="mc-agent-popover-range"
                  aria-label={scale.name}
                  min={0}
                  max={scale.values.length - 1}
                  step={1}
                  value={at(scale.values, draft[scale.key])}
                  onChange={event => move(scale.key, event.target.value)}
                  onPointerUp={release}
                  onKeyUp={release}
                />
              </div>
            ))}
          </div>
        </>
      )}
    </div>,
    document.body
  );
}
